import { clients } from 'lib/https';

import { getConfigurationSettings } from 'services/configuration-service';

import type {
  IAddQnAPayload,
  IAddQnAResponse,
  IAddFilesPayload,
  IAddFilesResponse,
  IAddLinkPayload,
  IAddLinkResponse,
  IAddTextPayload,
  IAddTextResponse,
  ICreateFolderPayload,
  ICreateFolderResponse,
  IDeleteFolderPayload,
  IDeleteFolderResponse,
  IDeleteKBPayload,
  IDeleteKBResponse,
  IReembedKBPayload,
  IReembedKBResponse,
  IGetAgentsByFolderIdPayload,
  IGetAgentsByFolderIdResponse,
  IGetAllKBPayload,
  IGetAllKBResponse,
  IGetFolderListResponse,
  IUpdateFolderPayload,
  IUpdateFolderResponse,
  IUpdateKBPayload,
  IUpdateKBResponse,
} from '../types/kb.service.type';

export class KBService {
  async getAllKB(payload: IGetAllKBPayload, blocksKey: string): Promise<IGetAllKBResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbKnowledges}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async addFiles(payload: IAddFilesPayload, blocksKey: string): Promise<IAddFilesResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFile}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async addText(payload: IAddTextPayload, blocksKey: string): Promise<IAddTextResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbText}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async addQnA(payload: IAddQnAPayload, blocksKey: string): Promise<IAddQnAResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbQa}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async addLink(payload: IAddLinkPayload, blocksKey: string): Promise<IAddLinkResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbLink}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async createFolder(
    payload: ICreateFolderPayload,
    blocksKey: string
  ): Promise<ICreateFolderResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFolderCreate}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async updateKb(payload: IUpdateKBPayload, blocksKey: string): Promise<IUpdateKBResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbUpdate}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async updateFolder(
    payload: IUpdateFolderPayload,
    blocksKey: string
  ): Promise<IUpdateFolderResponse> {
    const config = await getConfigurationSettings();

    return clients.put(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFolderUpdate}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async getFolderList(
    projectKey: string,
    blocksKey: string,
    search?: string
  ): Promise<IGetFolderListResponse> {
    const config = await getConfigurationSettings();
    const params = new URLSearchParams({ project_key: projectKey });
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }
    const url = `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFoldersList}?${params.toString()}`;

    return clients.get(url, blocksKey);
  }

  async deleteFolder(
    payload: IDeleteFolderPayload,
    blocksKey: string
  ): Promise<IDeleteFolderResponse> {
    const config = await getConfigurationSettings();
    const url =
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFolderDelete}` +
      `?folder_id=${encodeURIComponent(payload.folder_id)}&project_key=${encodeURIComponent(payload.project_key)}`;

    return clients.delete(url, blocksKey);
  }

  async deleteKbById(payload: IDeleteKBPayload, blocksKey: string): Promise<IDeleteKBResponse> {
    const config = await getConfigurationSettings();
    const url =
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbDelete}` +
      `/${encodeURIComponent(payload.kb_id)}` +
      `?project_key=${encodeURIComponent(payload.project_key)}`;

    return clients.delete(url, blocksKey);
  }

  async reembedKb(payload: IReembedKBPayload, blocksKey: string): Promise<IReembedKBResponse> {
    const config = await getConfigurationSettings();

    return clients.post(
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbReembed}`,
      blocksKey,
      JSON.stringify(payload)
    );
  }

  async getAgentsByFolderId(
    payload: IGetAgentsByFolderIdPayload,
    blocksKey: string
  ): Promise<IGetAgentsByFolderIdResponse> {
    const config = await getConfigurationSettings();
    const url =
      `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.kbFolderAgents}` +
      `?folder_id=${encodeURIComponent(payload.folder_id)}&project_key=${encodeURIComponent(payload.project_key)}`;

    return clients.get(url, blocksKey);
  }
}

export const kbService = new KBService();
